import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import followUpQuestions from "../../../../assets/imgs/follow_up_questions.svg";
import followUp from "../../../../assets/imgs/follow_up_icon.svg";
import { skeletonWidths } from '../Services';
import SkeletonLoader from './SkeletonText';

interface RelatedQuestionsProps {
  relatedQuestions: string[];
  followUpQuestionsLoading: boolean;
  onQuestionClick: (question: string) => void;
  typingLoading: boolean;
}

const RelatedQuestions: React.FC<RelatedQuestionsProps> = ({
  relatedQuestions,
  followUpQuestionsLoading,
  onQuestionClick,
  typingLoading
}) => {
  if (!(relatedQuestions.length > 0 || followUpQuestionsLoading) || typingLoading) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div 
        className="pt-[25px] pb-[70px]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <motion.div 
          className="flex mt-5 gap-[10px] items-center"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
        >
          <img src={followUpQuestions} alt="followUpQuestions" className='w-[18.72px] h-[17px]' />
          <span className="text-[13px] text-white font-semibold">
            Follow Up Questions
          </span>
        </motion.div>

        <div className="flex-col space-y-[7px] mt-[10px]">
          {followUpQuestionsLoading ? (
            <SkeletonLoader widths={skeletonWidths} />
          ) : (
            <AnimatePresence>
              {relatedQuestions.map((question, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -15, scale: 0.98 }}
                  transition={{ 
                    delay: index * 0.1 + 0.2, 
                    duration: 0.4, 
                    ease: "easeOut",
                    type: "spring",
                    stiffness: 200,
                    damping: 20
                  }}
                  whileHover={{ 
                    scale: 1.02, 
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onQuestionClick(question)}
                  className="flex justify-between gap-3 items-center border-b border-white/10 py-3 pr-1 cursor-pointer transition-colors"
                >
                  <motion.span 
                    className="text-white/70 text-[13px] font-medium"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3, duration: 0.3 }}
                  >
                    {question}
                  </motion.span>
                  <motion.img 
                    src={followUp} 
                    alt="rightArrow"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.4, duration: 0.3 }}
                    whileHover={{ x: 5 }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        <motion.div
          id="chat-section"
          className="flex flow-row mt-5 mb-3 items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        ></motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RelatedQuestions; 